// ScatterPlotFilters.js
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import Select from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import ScatterPlot from "./ScatterPlot";
import { formateTimeRange, resetState, loadFromState, saveToState } from "../utils";
import { getProfiles, getNodes, getProfilerMetadata } from "../api/quontxApi";

const ITEMS_PER_PAGE = 25;

// IMPORTANT:
// Backend may return proxy profile under this UUID (shared profile).
// We do NOT send this UUID in requests anymore.
const PROXY_PROFILE_UUID = "5666258c-3f67-4f16-8167-bff4a1cac760";

const ScatterPlotFilters = ({
  timestampBuckets = [],
  domains = [],
  isLoading = false,
  propagnateData,
}) => {
  const maxDomainCount = 2;
  const maxEntityCount = 10;
  const propagnateFunctionRef = useRef(propagnateData);

  const [data, setData] = useState({});
  const [loadingData, setLoadingData] = useState(false);

  const [activeTimestampBucket, setActiveTimestampBucket] = useState(null);
  const [activeDomains, setActiveDomains] = useState([]);
  const [activeEntities, setActiveEntities] = useState([]);
  const [activeProxies, setActiveProxies] = useState([]);

  const [domainSelectDisabled, setDomainSelectDisabled] = useState(false);
  const [entitySelectDisabled, setEntitySelectDisabled] = useState(false);
  const [proxySelectDisabled, setProxySelectDisabled] = useState(false);

  // proxy bucket handling
  const [proxyBuckets, setProxyBuckets] = useState([]);
  const [loadingBuckets, setLoadingBuckets] = useState(false);

  const hasAnySelected =
    (activeEntities?.length ?? 0) > 0 || (activeProxies?.length ?? 0) > 0;

  // ---------------------------
  // Selection handlers
  // ---------------------------

  const manageActiveDomains = (selectedDomains) => {
    const safe = selectedDomains ?? [];
    if (safe.length <= maxDomainCount) {
      setDomainSelectDisabled(false);
      setActiveDomains(safe.map((d) => ({ label: d.label, value: d.value })));
      return;
    }
    setDomainSelectDisabled(true);
  };

  const manageActiveEntities = (selectedEntities) => {
    const safe = selectedEntities ?? [];
    if (safe.length + (activeProxies?.length ?? 0) <= maxEntityCount) {
      setActiveEntities(safe.map((e) => ({ label: e.label, value: e.value })));
    }
  };

  const manageActiveProxies = (selectedProxies) => {
    const safe = selectedProxies ?? [];
    if ((activeEntities?.length ?? 0) + safe.length <= maxEntityCount) {
      setActiveProxies(safe.map((p) => ({ label: p.label, value: p.value })));
    }
  };

  const resetFilters = () => {
    resetState("scatterPlotState", [
      { stateFunc: setActiveTimestampBucket, nullVal: null },
      { stateFunc: setActiveDomains, nullVal: [] },
      { stateFunc: setActiveEntities, nullVal: [] },
      { stateFunc: setActiveProxies, nullVal: [] },
    ]);
    setDomainSelectDisabled(false);
    setEntitySelectDisabled(false);
    setProxySelectDisabled(false);
    setProxyBuckets([]);
    setData({});
  };

  // ---------------------------
  // Server-side search
  // ---------------------------

  const toOption = (node, disabled) => ({
    label: node.name,
    value: node.uuid,
    isDisabled: disabled,
  });

  const fetchEntitiesPage = useCallback(async ({ search, page }) => {
    const res = await getNodes({
      node_type: "NAMED_ENTITY:ORGANISATION",
      name: search,
      page,
      itemsPerPage: ITEMS_PER_PAGE,
    });
    return res?.data?.data ?? [];
  }, []);

  const fetchProxiesPage = useCallback(async ({ search, page }) => {
    const res = await getNodes({
      node_type: "NODE_SET",
      name: search,
      page,
      itemsPerPage: ITEMS_PER_PAGE,
    });
    return res?.data?.data ?? [];
  }, []);

  const loadEntityOptions = useCallback(
    async (search, _, additional) => {
      const q = (search || "").trim();
      if (q.length < 2) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      const page = additional?.page ?? 1;
      const items = await fetchEntitiesPage({ search: q, page });

      return {
        options: items.map((n) => toOption(n, entitySelectDisabled)),
        hasMore: items.length === ITEMS_PER_PAGE,
        additional: { page: page + 1 },
      };
    },
    [fetchEntitiesPage, entitySelectDisabled]
  );

  const loadProxyOptions = useCallback(
    async (search, _, additional) => {
      const q = (search || "").trim();
      if (q.length < 2) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      const page = additional?.page ?? 1;
      const items = await fetchProxiesPage({ search: q, page });

      return {
        options: items.map((n) => toOption(n, proxySelectDisabled)),
        hasMore: items.length === ITEMS_PER_PAGE,
        additional: { page: page + 1 },
      };
    },
    [fetchProxiesPage, proxySelectDisabled]
  );

  const entityAdditional = useMemo(() => ({ page: 1 }), []);
  const proxyAdditional = useMemo(() => ({ page: 1 }), []);

  // ---------------------------
  // Proxy timestamp buckets
  // ---------------------------

  const fetchProfilerBucketsForProxies = useCallback(async (proxyIds) => {
    if (!proxyIds?.length) return [];
    const allIntervals = [];

    for (const id of proxyIds) {
      const res = await getProfilerMetadata({
        itemsPerPage: 1,
        entities: [id],
      });

      const rows = res?.data?.data ?? res?.data ?? [];
      rows.forEach((row) => {
        allIntervals.push(...(row?.metadata?.corpora?.time_intervals ?? []));
      });
    }

    return Array.from(new Set(allIntervals));
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const proxyIds = (activeProxies ?? []).map((p) => p.value);
      if (proxyIds.length === 0) {
        setProxyBuckets([]);
        return;
      }

      setLoadingBuckets(true);
      try {
        const buckets = await fetchProfilerBucketsForProxies(proxyIds);
        if (cancelled) return;

        setProxyBuckets(buckets);
        if (activeTimestampBucket && !buckets.includes(activeTimestampBucket)) {
          setActiveTimestampBucket(null);
        }
      } finally {
        if (!cancelled) setLoadingBuckets(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeProxies, fetchProfilerBucketsForProxies, activeTimestampBucket]);

  const availableTimestampBuckets = useMemo(() => {
    return (activeProxies?.length ?? 0) > 0 ? proxyBuckets : timestampBuckets ?? [];
  }, [activeProxies, proxyBuckets, timestampBuckets]);

  // ---------------------------
  // Load profiles
  // ---------------------------

  const loadProfiles = useCallback(async () => {
    const entityIds = (activeEntities ?? []).map((e) => e.value);
    const proxyIds = (activeProxies ?? []).map((p) => p.value);

    // ✅ Always request *real* selected UUIDs. Never the hardcoded proxy UUID.
    const requestIds = [...entityIds, ...proxyIds];

    if (requestIds.length === 0) {
      setData({});
      return;
    }

    const profiles = await getProfiles({
      itemsPerPage: Math.max(requestIds.length, 10),
      entities: requestIds,
    });

    propagnateFunctionRef.current(profiles.data);

    const rows = profiles?.data?.data ?? [];
    const profileByUuid = {};
    for (const p of rows) {
      const uuid = p?.metadata?.corpora?.uuid;
      if (uuid) profileByUuid[uuid] = p.values;
    }

    // If backend returns proxy profile under shared UUID, use it as fallback for proxies.
    const sharedProxyProfile = profileByUuid[PROXY_PROFILE_UUID] ?? null;

    const next = {};

    // Entities map 1:1
    for (const id of entityIds) {
      const prof = profileByUuid[id];
      if (prof) next[id] = prof;
      else console.warn("[ScatterPlotFilters] Missing entity profile", id);
    }

    // Proxies: prefer profileByUuid[proxyId], else fall back to sharedProxyProfile
    for (const pid of proxyIds) {
      const prof = profileByUuid[pid] ?? sharedProxyProfile;
      if (prof) next[pid] = prof;
      else console.warn("[ScatterPlotFilters] Missing proxy profile", pid);
    }

    setData(next);
  }, [activeEntities, activeProxies]);

  useEffect(() => {
    const total = (activeEntities?.length ?? 0) + (activeProxies?.length ?? 0);
    if (total < maxEntityCount) {
      setEntitySelectDisabled(false);
      setProxySelectDisabled(false);
    } else {
      setEntitySelectDisabled(true);
      setProxySelectDisabled(true);
    }
  }, [activeEntities, activeProxies]);

  // ---------------------------
  // localStorage
  // ---------------------------

  useEffect(() => {
    loadFromState("scatterPlotState", [
      {
        stateFunc: setActiveTimestampBucket,
        storageObjectKey: "activeTimestampBucket",
        defaultVal: null,
      },
      { stateFunc: setActiveDomains, storageObjectKey: "activeDomains", defaultVal: [] },
      { stateFunc: setActiveEntities, storageObjectKey: "activeEntities", defaultVal: [] },
      { stateFunc: setActiveProxies, storageObjectKey: "activeProxies", defaultVal: [] },
    ]);

    const f = propagnateFunctionRef.current;
    return () => f({});
  }, []);

  useEffect(() => {
    if (
      activeTimestampBucket !== null ||
      activeDomains.length ||
      activeEntities.length ||
      activeProxies.length
    ) {
      saveToState("scatterPlotState", {
        activeTimestampBucket,
        activeDomains,
        activeEntities,
        activeProxies,
      });
    }
  }, [activeTimestampBucket, activeDomains, activeEntities, activeProxies]);

  useEffect(() => {
    const total = (activeEntities?.length ?? 0) + (activeProxies?.length ?? 0);

    // ✅ If nothing selected: never fetch (and clear data)
    if (total === 0) {
      setData({});
      return;
    }

    (async () => {
      setLoadingData(true);
      try {
        await loadProfiles();
      } finally {
        setLoadingData(false);
      }
    })();
  }, [activeEntities, activeProxies, loadProfiles]);

  // ---------------------------
  // Derived
  // ---------------------------

  const timestampOptions = useMemo(() => {
    return (availableTimestampBuckets ?? []).map((tb, idx) => ({
      label: formateTimeRange(tb),
      value: tb,
      isDisabled: idx < 10,
    }));
  }, [availableTimestampBuckets]);

  const computedTimebucketIdx = useMemo(() => {
    if (!activeTimestampBucket) return -1;
    return availableTimestampBuckets.indexOf(activeTimestampBucket);
  }, [activeTimestampBucket, availableTimestampBuckets]);

  // ---------------------------
  // Render
  // ---------------------------

  return (
    <div className="w-full flex flex-col items-center">
      {isLoading && <p>Loading data...</p>}

      {!isLoading && (domains?.length ?? 0) > 0 && (
        <div className="flex flex-col gap-4 items-center mb-8">
          <div className="flex gap-4 mb-8">
            <div className="w-[240px] flex flex-col gap-2">
              <p>Organization Profiles</p>
              <AsyncPaginate
                isMulti
                value={activeEntities}
                onChange={manageActiveEntities}
                loadOptions={loadEntityOptions}
                additional={entityAdditional}
                debounceTimeout={250}
                cacheOptions
              />
            </div>

            <div className="w-[240px] flex flex-col gap-2">
              <p>Organization Proxies</p>
              <AsyncPaginate
                isMulti
                value={activeProxies}
                onChange={manageActiveProxies}
                loadOptions={loadProxyOptions}
                additional={proxyAdditional}
                debounceTimeout={250}
                cacheOptions
              />
            </div>
          </div>

          <div className="flex gap-4 mb-8">
            <div className="w-[240px] flex flex-col gap-2">
              <p>Societal Domains</p>
              <Select
                isDisabled={!hasAnySelected}
                isMulti
                value={activeDomains}
                onChange={manageActiveDomains}
                options={(domains ?? []).map((d) => ({
                  label: d.description,
                  value: d.uuid,
                  isDisabled: domainSelectDisabled,
                }))}
              />
            </div>

            <div className="w-[240px] flex flex-col gap-2">
              <p>Timestamp Bucket</p>
              <Select
                isDisabled={
                  !hasAnySelected ||
                  loadingBuckets ||
                  availableTimestampBuckets.length === 0
                }
                isSearchable={false}
                value={
                  activeTimestampBucket
                    ? { label: activeTimestampBucket, value: activeTimestampBucket }
                    : null
                }
                onChange={(s) => setActiveTimestampBucket(s?.value ?? null)}
                options={timestampOptions}
              />
            </div>
          </div>

          <button
            onClick={resetFilters}
            className="bg-blue-500 text-white py-2 px-4 rounded"
          >
            Reset filters
          </button>

          {activeDomains.length === 2 &&
            hasAnySelected &&
            activeTimestampBucket &&
            computedTimebucketIdx >= 0 &&
            Object.keys(data).length > 0 && (
              <ScatterPlot
                isLoading={loadingData}
                data={data}
                timebucketIdx={computedTimebucketIdx}
                activeDomains={activeDomains}
                activeEntities={activeEntities}
                activeProxies={activeProxies}
              />
            )}
        </div>
      )}
    </div>
  );
};

export default ScatterPlotFilters;